// Domain categories and subcategories mapping for expertise selection
export interface SubcategoryOption {
  name: string;
  description?: string;
}

export interface CategoryOption {
  name: string;
  subcategories: SubcategoryOption[];
}

export const domainCategoriesMap: Record<string, CategoryOption[]> = {
  "Auto Construction": [
    {
      name: "Drones & UAVs",
      subcategories: [
        { name: "Site Surveying" },
        { name: "Progress Monitoring" },
        { name: "Aerial Inspection" },
        { name: "Thermal Imaging" },
        { name: "Photogrammetry" }
      ]
    },
    {
      name: "Autonomous Vehicles",
      subcategories: [
        { name: "Material Transport" },
        { name: "Site Navigation" },
        { name: "Heavy Equipment Automation" },
        { name: "Fleet Management" },
        { name: "Safety Systems" }
      ]
    },
    {
      name: "Robotics",
      subcategories: [
        { name: "Bricklaying Robots" },
        { name: "Welding Automation" },
        { name: "Demolition Robots" },
        { name: "Concrete Finishing" },
        { name: "Material Handling" }
      ]
    },
    {
      name: "IoT Monitoring",
      subcategories: [
        { name: "Sensor Networks" },
        { name: "Environmental Monitoring" },
        { name: "Equipment Tracking" },
        { name: "Real-time Alerts" },
        { name: "Data Analytics Integration" }
      ]
    }
  ],
  "Automation": [
    {
      name: "BIM",
      subcategories: [
        { name: "3D Modeling" },
        { name: "4D Scheduling" },
        { name: "5D Cost Estimation" },
        { name: "Clash Detection" },
        { name: "BIM Coordination" }
      ]
    },
    {
      name: "Digital Twins",
      subcategories: [
        { name: "Virtual Commissioning" },
        { name: "Predictive Maintenance" },
        { name: "Performance Simulation" },
        { name: "Real-time Monitoring" },
        { name: "Lifecycle Management" }
      ]
    },
    {
      name: "Construction Robotics",
      subcategories: [
        { name: "Automated Fabrication" },
        { name: "Robotic Assembly" },
        { name: "Quality Inspection Bots" },
        { name: "Painting & Coating" },
        { name: "Rebar Tying" }
      ]
    },
    {
      name: "AI-driven Management",
      subcategories: [
        { name: "Schedule Optimization" },
        { name: "Resource Allocation" },
        { name: "Risk Prediction" },
        { name: "Decision Support Systems" },
        { name: "Workflow Automation" }
      ]
    }
  ],
  "Data Analytics": [
    {
      name: "Real-time Monitoring",
      subcategories: [
        { name: "Dashboard Development" },
        { name: "KPI Tracking" },
        { name: "Performance Metrics" },
        { name: "Alert Systems" },
        { name: "Visualization Tools" }
      ]
    },
    {
      name: "Predictive Analytics",
      subcategories: [
        { name: "Machine Learning Models" },
        { name: "Forecasting Techniques" },
        { name: "Trend Analysis" },
        { name: "Risk Prediction" },
        { name: "Resource Optimization" }
      ]
    },
    {
      name: "AI Optimization",
      subcategories: [
        { name: "Process Automation" },
        { name: "Schedule Optimization" },
        { name: "Cost Reduction" },
        { name: "Quality Enhancement" },
        { name: "Supply Chain Optimization" }
      ]
    },
    {
      name: "Data Integration",
      subcategories: [
        { name: "System Integration" },
        { name: "Data Warehousing" },
        { name: "API Development" },
        { name: "Data Quality Management" },
        { name: "Cloud Platforms" }
      ]
    }
  ],
  "Financial Control": [
    {
      name: "Value Engineering",
      subcategories: [
        { name: "Cost-Benefit Analysis" },
        { name: "Alternative Assessment" },
        { name: "Function Analysis" },
        { name: "Life Cycle Costing" },
        { name: "Optimization Strategies" }
      ]
    },
    {
      name: "Cost Forecasting",
      subcategories: [
        { name: "Budget Estimation" },
        { name: "Trend Analysis" },
        { name: "Historical Data Analysis" },
        { name: "Variance Analysis" },
        { name: "Contingency Planning" }
      ]
    },
    {
      name: "Earned Value Management",
      subcategories: [
        { name: "Cost Performance Index" },
        { name: "Schedule Performance Index" },
        { name: "Estimate at Completion" },
        { name: "Progress Tracking" },
        { name: "Performance Reporting" }
      ]
    },
    {
      name: "Financial Risk Management",
      subcategories: [
        { name: "Risk Identification" },
        { name: "Financial Modeling" },
        { name: "Mitigation Strategies" },
        { name: "Insurance Planning" },
        { name: "Cash Flow Management" }
      ]
    }
  ],
  "Health & Safety": [
    {
      name: "Safety Standards",
      subcategories: [
        { name: "OSHA Compliance" },
        { name: "Safety Regulations" },
        { name: "Industry Best Practices" },
        { name: "Safety Protocols" },
        { name: "Documentation Requirements" }
      ]
    },
    {
      name: "Hazardous Material Management",
      subcategories: [
        { name: "Material Identification" },
        { name: "Handling Procedures" },
        { name: "Storage Requirements" },
        { name: "Disposal Methods" },
        { name: "Emergency Response" }
      ]
    },
    {
      name: "Emergency Response",
      subcategories: [
        { name: "Emergency Planning" },
        { name: "Evacuation Procedures" },
        { name: "First Aid Training" },
        { name: "Incident Command" },
        { name: "Crisis Communication" }
      ]
    },
    {
      name: "Safety Training",
      subcategories: [
        { name: "Worker Safety Programs" },
        { name: "Equipment Training" },
        { name: "Site Safety Audits" },
        { name: "Toolbox Talks" },
        { name: "Safety Certifications" }
      ]
    },
    {
      name: "Safety Technology",
      subcategories: [
        { name: "Wearable Sensors" },
        { name: "AI Safety Monitoring" },
        { name: "Digital Safety Management" },
        { name: "VR Safety Training" },
        { name: "Incident Tracking Systems" }
      ]
    }
  ],
  "Innovation Tech": [
    {
      name: "Smart Buildings",
      subcategories: [
        { name: "Building Automation Systems" },
        { name: "Energy Management" },
        { name: "IoT Integration" },
        { name: "Occupancy Optimization" },
        { name: "Predictive Maintenance" }
      ]
    },
    {
      name: "Robotics",
      subcategories: [
        { name: "Construction Automation" },
        { name: "Inspection Robots" },
        { name: "Material Handling" },
        { name: "Collaborative Robots" },
        { name: "Autonomous Systems" }
      ]
    },
    {
      name: "AI Optimization",
      subcategories: [
        { name: "Machine Learning Applications" },
        { name: "Computer Vision" },
        { name: "Natural Language Processing" },
        { name: "Decision Support Systems" },
        { name: "Process Automation" }
      ]
    },
    {
      name: "Emerging Technologies",
      subcategories: [
        { name: "Augmented Reality" },
        { name: "Virtual Reality" },
        { name: "Blockchain Applications" },
        { name: "Quantum Computing" },
        { name: "Advanced Materials" }
      ]
    }
  ],
  "Project Management": [
    {
      name: "Project Scoping",
      subcategories: [
        { name: "Requirements Gathering" },
        { name: "Feasibility Studies" },
        { name: "Stakeholder Analysis" },
        { name: "Scope Definition" },
        { name: "Constraint Identification" }
      ]
    },
    {
      name: "Scheduling",
      subcategories: [
        { name: "Critical Path Method (CPM)" },
        { name: "Program Evaluation Review Technique (PERT)" },
        { name: "Gantt Charts" },
        { name: "Resource Leveling" },
        { name: "Timeline Optimization" }
      ]
    },
    {
      name: "Resource Allocation",
      subcategories: [
        { name: "Labor Planning" },
        { name: "Equipment Scheduling" },
        { name: "Material Procurement" },
        { name: "Budget Distribution" },
        { name: "Resource Optimization" }
      ]
    },
    {
      name: "Risk Management",
      subcategories: [
        { name: "Risk Identification" },
        { name: "Risk Assessment" },
        { name: "Mitigation Strategies" },
        { name: "Contingency Planning" },
        { name: "Risk Monitoring" }
      ]
    },
    {
      name: "Budgeting",
      subcategories: [
        { name: "Cost Estimation" },
        { name: "Budget Forecasting" },
        { name: "Cost Control" },
        { name: "Financial Reporting" },
        { name: "Value Engineering" }
      ]
    }
  ],
  "Engineering & Design": [
    {
      name: "Structural Design",
      subcategories: [
        { name: "Load Analysis" },
        { name: "Foundation Design" },
        { name: "Steel Structures" },
        { name: "Concrete Design" },
        { name: "Seismic Design" }
      ]
    },
    {
      name: "Civil Engineering",
      subcategories: [
        { name: "Site Development" },
        { name: "Earthwork" },
        { name: "Drainage Systems" },
        { name: "Road Design" },
        { name: "Geotechnical Engineering" }
      ]
    },
    {
      name: "MEP",
      subcategories: [
        { name: "HVAC Systems" },
        { name: "Electrical Systems" },
        { name: "Plumbing Design" },
        { name: "Fire Protection" },
        { name: "Energy Management" }
      ]
    },
    {
      name: "Architectural Design",
      subcategories: [
        { name: "Space Planning" },
        { name: "Building Codes Compliance" },
        { name: "Facade Design" },
        { name: "Interior Design" },
        { name: "3D Visualization" }
      ]
    }
  ],
  "Construction Methods": [
    {
      name: "Modular Construction",
      subcategories: [
        { name: "Prefabrication Techniques" },
        { name: "Module Assembly" },
        { name: "Transportation Logistics" },
        { name: "On-site Integration" },
        { name: "Quality Control" }
      ]
    },
    {
      name: "Prefabrication",
      subcategories: [
        { name: "Off-site Manufacturing" },
        { name: "Component Design" },
        { name: "Factory Production" },
        { name: "Lean Manufacturing" },
        { name: "Just-in-Time Delivery" }
      ]
    },
    {
      name: "3D Printing",
      subcategories: [
        { name: "Concrete 3D Printing" },
        { name: "Material Science" },
        { name: "Print Path Planning" },
        { name: "Structural Validation" },
        { name: "Post-Processing" }
      ]
    },
    {
      name: "Traditional Construction",
      subcategories: [
        { name: "Masonry" },
        { name: "Carpentry" },
        { name: "Cast-in-Place Concrete" },
        { name: "Steel Erection" },
        { name: "Site Management" }
      ]
    }
  ],
  "Supply Chain": [
    {
      name: "Vendor Management",
      subcategories: [
        { name: "Supplier Selection" },
        { name: "Contract Negotiation" },
        { name: "Performance Monitoring" },
        { name: "Relationship Management" },
        { name: "Quality Assurance" }
      ]
    },
    {
      name: "Material Sourcing",
      subcategories: [
        { name: "Material Selection" },
        { name: "Procurement Strategy" },
        { name: "Supply Chain Optimization" },
        { name: "Cost Analysis" },
        { name: "Sustainability Sourcing" }
      ]
    },
    {
      name: "Contract Negotiation",
      subcategories: [
        { name: "Terms & Conditions" },
        { name: "Price Negotiation" },
        { name: "Legal Compliance" },
        { name: "Risk Allocation" },
        { name: "Performance Clauses" }
      ]
    },
    {
      name: "Logistics Management",
      subcategories: [
        { name: "Transportation Planning" },
        { name: "Inventory Management" },
        { name: "Warehouse Operations" },
        { name: "Delivery Scheduling" },
        { name: "Supply Chain Visibility" }
      ]
    }
  ],
  "Green Building": [
    {
      name: "LEED Certification",
      subcategories: [
        { name: "LEED Documentation" },
        { name: "Credit Optimization" },
        { name: "Energy Modeling" },
        { name: "Material Credits" },
        { name: "Indoor Environmental Quality" }
      ]
    },
    {
      name: "Sustainable Materials",
      subcategories: [
        { name: "Recycled Content" },
        { name: "Regional Materials" },
        { name: "Low VOC Products" },
        { name: "Rapidly Renewable Materials" },
        { name: "Life Cycle Assessment" }
      ]
    },
    {
      name: "Carbon Footprint Reduction",
      subcategories: [
        { name: "Embodied Carbon Analysis" },
        { name: "Operational Carbon" },
        { name: "Carbon Sequestration" },
        { name: "Net Zero Strategies" },
        { name: "Carbon Offsetting" }
      ]
    },
    {
      name: "Energy Efficiency",
      subcategories: [
        { name: "Building Envelope" },
        { name: "Renewable Energy Systems" },
        { name: "Energy Monitoring" },
        { name: "Passive Design" },
        { name: "High Performance HVAC" }
      ]
    }
  ],
  "Quality Assurance": [
    {
      name: "Inspection",
      subcategories: [
        { name: "Quality Inspections" },
        { name: "Field Testing" },
        { name: "Non-Destructive Testing" },
        { name: "Visual Inspection" },
        { name: "Documentation" }
      ]
    },
    {
      name: "Testing",
      subcategories: [
        { name: "Material Testing" },
        { name: "Structural Testing" },
        { name: "Performance Testing" },
        { name: "Commissioning" },
        { name: "Laboratory Analysis" }
      ]
    },
    {
      name: "ISO Certification",
      subcategories: [
        { name: "ISO 9001 Quality Management" },
        { name: "ISO 14001 Environmental" },
        { name: "ISO 45001 Safety" },
        { name: "Certification Process" },
        { name: "Compliance Audits" }
      ]
    },
    {
      name: "Quality Audits",
      subcategories: [
        { name: "Internal Audits" },
        { name: "External Audits" },
        { name: "Process Audits" },
        { name: "Corrective Actions" },
        { name: "Continuous Improvement" }
      ]
    },
    {
      name: "Quality Control",
      subcategories: [
        { name: "Quality Plans" },
        { name: "Control Procedures" },
        { name: "Defect Management" },
        { name: "Rework Prevention" },
        { name: "Quality Metrics" }
      ]
    }
  ],
  "Stakeholder Eng": [
    {
      name: "Client Communication",
      subcategories: [
        { name: "Regular Reporting" },
        { name: "Progress Updates" },
        { name: "Expectation Management" },
        { name: "Issue Resolution" },
        { name: "Relationship Building" }
      ]
    },
    {
      name: "Change Management",
      subcategories: [
        { name: "Change Request Process" },
        { name: "Impact Assessment" },
        { name: "Approval Workflows" },
        { name: "Documentation" },
        { name: "Stakeholder Communication" }
      ]
    },
    {
      name: "Reporting Systems",
      subcategories: [
        { name: "Dashboard Development" },
        { name: "KPI Reporting" },
        { name: "Executive Summaries" },
        { name: "Financial Reports" },
        { name: "Performance Metrics" }
      ]
    },
    {
      name: "Stakeholder Analysis",
      subcategories: [
        { name: "Stakeholder Identification" },
        { name: "Interest Assessment" },
        { name: "Influence Mapping" },
        { name: "Engagement Planning" },
        { name: "Conflict Resolution" }
      ]
    },
    {
      name: "Community Engagement",
      subcategories: [
        { name: "Public Consultation" },
        { name: "Community Relations" },
        { name: "Social Impact Assessment" },
        { name: "Local Partnerships" },
        { name: "Outreach Programs" }
      ]
    }
  ],
  "Sustainability": [
    {
      name: "Sustainable Practices",
      subcategories: [
        { name: "Green Construction Methods" },
        { name: "Water Conservation" },
        { name: "Sustainable Site Development" },
        { name: "Biodiversity Protection" },
        { name: "Circular Economy" }
      ]
    },
    {
      name: "Waste Reduction",
      subcategories: [
        { name: "Waste Management Plans" },
        { name: "Material Reuse" },
        { name: "Recycling Programs" },
        { name: "Construction Waste Minimization" },
        { name: "Demolition Waste Recovery" }
      ]
    },
    {
      name: "Energy Efficiency",
      subcategories: [
        { name: "Energy Audits" },
        { name: "Efficiency Measures" },
        { name: "Renewable Energy Integration" },
        { name: "Energy Management Systems" },
        { name: "Performance Monitoring" }
      ]
    },
    {
      name: "Environmental Compliance",
      subcategories: [
        { name: "Environmental Regulations" },
        { name: "Permit Management" },
        { name: "Environmental Impact Assessment" },
        { name: "Remediation" },
        { name: "Monitoring & Reporting" }
      ]
    },
    {
      name: "Sustainable Materials",
      subcategories: [
        { name: "Material Selection" },
        { name: "Local Sourcing" },
        { name: "Recycled Materials" },
        { name: "Life Cycle Analysis" },
        { name: "Material Certifications" }
      ]
    }
  ],
  "Workforce Dev": [
    {
      name: "Skills Development",
      subcategories: [
        { name: "Technical Training" },
        { name: "Trade Skills" },
        { name: "Certification Programs" },
        { name: "Apprenticeships" },
        { name: "Continuing Education" }
      ]
    },
    {
      name: "Safety Training",
      subcategories: [
        { name: "OSHA Training" },
        { name: "Site Safety Orientation" },
        { name: "Equipment Safety" },
        { name: "Hazard Recognition" },
        { name: "Emergency Procedures" }
      ]
    },
    {
      name: "Digital Skills",
      subcategories: [
        { name: "BIM Training" },
        { name: "Construction Software" },
        { name: "Digital Tools" },
        { name: "Data Analytics" },
        { name: "Technology Adoption" }
      ]
    },
    {
      name: "Project Management",
      subcategories: [
        { name: "Leadership Development" },
        { name: "Project Planning" },
        { name: "Team Management" },
        { name: "Communication Skills" },
        { name: "Problem Solving" }
      ]
    },
    {
      name: "Career Development",
      subcategories: [
        { name: "Career Pathways" },
        { name: "Mentorship Programs" },
        { name: "Professional Development" },
        { name: "Performance Management" },
        { name: "Succession Planning" }
      ]
    }
  ]
};

// Helper function to get categories for a domain
export const getCategoriesForDomain = (domainName: string): CategoryOption[] => {
  return domainCategoriesMap[domainName] || [];
};

// Helper function to get subcategories for a domain and category
export const getSubcategoriesForCategory = (domainName: string, categoryName: string): SubcategoryOption[] => {
  const categories = domainCategoriesMap[domainName] || [];
  const category = categories.find(cat => cat.name === categoryName);
  return category?.subcategories || [];
};
